import type { Tool, ToolStatus } from "@/hooks/types";

/**
 * Where a catalogue entry stands in its own life — and, deliberately, nothing
 * about who may use the tool (Jira NAV-13).
 *
 * The distinction is the whole of this module. `tools.status` is a statement
 * about the **record**: the group still keeps this entry, is retiring it, or has
 * withdrawn it. Availability — whether a person may have the tool — is a
 * *business unit's* statement, lives on that business unit's `tool_configs.status`, and is never
 * made at group level (principle 13, ADR 0012). So deprecating a tool is not a
 * group-wide block, and this module never gets to say one thing an availability
 * could contradict.
 *
 * What follows from that:
 *
 * - **DEPRECATED** is *marked, never enforced.* A member meets the tool with a
 *   badge saying the group is retiring it, and every action the page offered
 *   before it still works. Refusing the request instead would be a group-wide
 *   refusal wearing a lifecycle word, which is the one thing principle 13 rules
 *   out — and PRD A6 settled that retiring a tool involves no mass
 *   communication and no withdrawal of what people already hold.
 * - **ARCHIVED** is *withdrawn.* The entry stops being listed to members, so
 *   there is nothing to discover and nothing to propose. That is the absence of
 *   an entry rather than a refusal of one — and strictly gentler than the hard
 *   delete a super admin could already perform, which took the tool out of
 *   everyone's lists, holders included, with no way back.
 *
 * A member who already holds an archived tool keeps it and keeps its page, and
 * can still record that they have it: exclusion is from *discovery*, not from
 * the register.
 *
 * Admin surfaces exclude a withdrawn entry from **one** thing — a business unit's
 * "Adopt from catalogue", which is itself an offer — and from nothing else. An
 * admin who cannot see what they archived cannot bring it back, so every list
 * keeps showing it, marked.
 */

/**
 * The transitions the admin catalogue offers, in the order they are shown.
 *
 * `PENDING` is in `ToolStatus` and is not here: it is written by nothing —
 * neither the client's create (which stamps ACTIVE) nor the backend's `Tool`
 * default — so offering it would put a word on screen that means whatever the
 * next reader assumes. Anything outside this list is treated as current by
 * `isRetired`, so an entry carrying it behaves exactly as it does today.
 */
export const TOOL_LIFECYCLE_STATUSES: readonly ToolStatus[] = [
  "ACTIVE",
  "DEPRECATED",
  "ARCHIVED",
];

/**
 * Lifecycle pill colours, light and dark.
 *
 * Deliberately *not* the availability palette (`AVAILABILITY_PILL`): the two
 * vocabularies appear side by side on a member's tool page, and giving a
 * deprecated entry the amber of STANDARD or an archived one the red of BLOCKED
 * would read as an availability the group has no business stating. Slate and
 * stone say "about the record" rather than "about you".
 */
export const LIFECYCLE_PILL: Record<ToolStatus, string> = {
  ACTIVE:
    "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700",
  PENDING:
    "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700",
  DEPRECATED:
    "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",
  ARCHIVED:
    "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600",
};

/**
 * The vocabulary's own words: a **name** and a one-line **meaning** per status,
 * in the `common` bundle so the admin table, the member's badge and the tool
 * page cannot describe the same word differently — the same arrangement the
 * availability vocabulary settled on (Jira NAV-72).
 *
 * Total over `ToolStatus`, so a status added to the model fails to compile
 * until somebody writes down what it means.
 */
const LIFECYCLE_COPY: Record<ToolStatus, string> = {
  ACTIVE: "common:toolLifecycle.ACTIVE",
  PENDING: "common:toolLifecycle.PENDING",
  DEPRECATED: "common:toolLifecycle.DEPRECATED",
  ARCHIVED: "common:toolLifecycle.ARCHIVED",
};

/** What this status is called, wherever it is named. */
export function lifecycleLabelKey(status: ToolStatus): string {
  return `${LIFECYCLE_COPY[status]}.label`;
}

/**
 * A stored string read as a lifecycle status, or `null` when it is not one.
 *
 * Derived from `LIFECYCLE_COPY`, which is total over `ToolStatus`, rather than
 * from a hand-written list — a list would be a second place to add a status to,
 * and the one that gets forgotten. Deliberately not `TOOL_LIFECYCLE_STATUSES`
 * either: that is the set of *moves a menu offers*, which is a smaller thing
 * (`PENDING` is a real stored value nobody transitions to by hand).
 *
 * For readers that receive a status as data — a change record's `before`/`after`
 * — where indexing the copy map with an unchecked string would type-check and
 * hand `t()` an undefined key, which renders as the key itself.
 */
export function asToolStatus(value: string): ToolStatus | null {
  return value in LIFECYCLE_COPY ? (value as ToolStatus) : null;
}

/** What it means for whoever is reading it, in one line. */
export function lifecycleMeaningKey(status: ToolStatus): string {
  return `${LIFECYCLE_COPY[status]}.meaning`;
}

/** The subset of `Tool` the lifecycle predicates read. A caller holding only a
 *  status passes `{ status }`; every real caller holds the whole entry. */
type Lifecycle = Pick<Tool, "status">;

/**
 * The status of an entry, treating a missing or unrecognised one as ACTIVE.
 *
 * A `Tool` is a TypeScript claim about `snap.data()` and not a checked one, so
 * `status` can hold anything a document happens to carry — an entry written
 * before the field existed carries nothing at all. Reading that as retired
 * would take a live tool out of every member's catalogue on the strength of a
 * missing field, so the fallback goes the other way: unknown means current.
 */
export function lifecycleStatus(tool: Lifecycle): ToolStatus {
  const status = tool.status;
  return status === "DEPRECATED" || status === "ARCHIVED" || status === "PENDING"
    ? status
    : "ACTIVE";
}

/** Is the group retiring this entry — deprecated or already withdrawn? What
 *  every surface marks, and what nothing branches on beyond marking it. */
export function isRetiring(tool: Lifecycle): boolean {
  const status = lifecycleStatus(tool);
  return status === "DEPRECATED" || status === "ARCHIVED";
}

/**
 * Has the entry been withdrawn from the catalogue members read?
 *
 * The one predicate that removes something from a screen, so it is asked
 * narrowly: ARCHIVED alone. A deprecated tool is still listed, still
 * requestable, and merely marked — see this module's header for why the
 * gentler word carries no enforcement.
 */
export function isWithdrawn(tool: Lifecycle): boolean {
  return lifecycleStatus(tool) === "ARCHIVED";
}

/**
 * Should this entry appear where a member is *finding* tools — the catalogue,
 * its category counts, the request wizard's search?
 *
 * Not a permission check and not usable as one: a member who already holds an
 * archived tool still sees it in their own list and can still open its page.
 * What this narrows is discovery.
 */
export function listedForMembers(tool: Lifecycle): boolean {
  return !isWithdrawn(tool);
}
